import React, { useRef, useState } from "react";
import { Autocomplete, Box, Button, Checkbox, Chip, TextField, Typography } from "@mui/material";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { C } from "../reporting/reportingUtils";
import {
  PLANNED_INSURANCE_CARRIERS, isInsuranceCarrierDemoEnabled,
  normalizePlannedCarrierSelection, plannedCarrierStorageKey, readPlannedCarrierSelection,
} from "../config/insuranceCarrierCatalog";

// Scope and remount the draft before reading storage, including during admin tenant switches.
export default function InsuranceCarrierConnections({ tenantId }) {
  if (!isInsuranceCarrierDemoEnabled(tenantId)) return null;
  return <TenantCarrierSelection key={tenantId} tenantId={tenantId} />;
}

function TenantCarrierSelection({ tenantId }) {
  const inputRef = useRef(null);
  const [selection, setSelection] = useState(() => readPlannedCarrierSelection(tenantId));
  const selectedCarriers = PLANNED_INSURANCE_CARRIERS.filter((carrier) => selection.ids.includes(carrier.id));

  const updateSelection = (carriers) => {
    const ids = normalizePlannedCarrierSelection(carriers.map((carrier) => carrier.id));
    let storageAvailable = true;
    try {
      if (ids.length) window.localStorage.setItem(plannedCarrierStorageKey(tenantId), JSON.stringify(ids));
      else window.localStorage.removeItem(plannedCarrierStorageKey(tenantId));
    } catch (_) {
      storageAvailable = false;
    }
    setSelection({ ids, storageAvailable });
  };

  return (
    <Box component="section" aria-labelledby="insurer-connections-title" sx={{
      background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: { xs: 2, md: 2.5 }, mb: 2,
    }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "10px", flexShrink: 0, display: "grid", placeItems: "center", background: "#EDF3FF", color: C.blue }}>
          <HubOutlinedIcon aria-hidden="true" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography id="insurer-connections-title" component="h2" sx={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Insurer connections</Typography>
            <Chip label="Demo" size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, background: "#FFF3DE", color: "#865A12" }} />
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: C.muted }}>Choose insurers for future data connections.</Typography>
        </Box>
        <Typography role="status" aria-live="polite" aria-atomic="true" sx={{ fontSize: 12, fontWeight: 700, color: C.blue, pt: 0.5, whiteSpace: "nowrap" }}>
          {selectedCarriers.length} of {PLANNED_INSURANCE_CARRIERS.length} selected
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 260px" }, alignItems: "start", gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Autocomplete
            multiple disableCloseOnSelect openOnFocus
            id="planned-insurers"
            options={PLANNED_INSURANCE_CARRIERS}
            value={selectedCarriers}
            onChange={(_, carriers) => updateSelection(carriers)}
            getOptionLabel={(carrier) => carrier.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            clearText="Clear all selected insurers"
            openText="Show insurers"
            closeText="Hide insurers"
            noOptionsText="No insurers match your search. Try another name."
            ListboxProps={{ "aria-label": "Insurers available for planned connections", "aria-multiselectable": true }}
            renderOption={(props, carrier, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} tabIndex={-1} disableRipple size="small" aria-hidden="true" sx={{ mr: 0.75, p: 0.5 }} />
                <Typography component="span" sx={{ flex: 1, color: C.ink, fontSize: 13 }}>{carrier.label}</Typography>
                <Typography component="span" sx={{ ml: 2, fontSize: 11, color: C.muted }}>Planned</Typography>
              </li>
            )}
            renderTags={(carriers, getTagProps) => carriers.map((carrier, index) => (
              <Chip
                {...getTagProps({ index })}
                key={carrier.id}
                label={carrier.label}
                size="small"
                aria-label={`${carrier.label}, planned. Press Delete to remove.`}
                deleteIcon={<CloseIcon aria-label={`Remove ${carrier.label}`} />}
                sx={{ background: "#EDF3FF", color: "#2454AE", fontWeight: 600, maxWidth: "100%" }}
              />
            ))}
            renderInput={(params) => (
              <TextField {...params} inputRef={inputRef} label="Select insurers" placeholder={selectedCarriers.length ? "Search to add more" : "Search insurers…"}
                inputProps={{ ...params.inputProps, "aria-describedby": "planned-insurers-help planned-insurers-status" }} />
            )}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", minHeight: 56 }, "& .MuiAutocomplete-tag": { my: 0.5 } }}
          />
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mt: 1 }}>
            <Typography id="planned-insurers-help" sx={{ fontSize: 12, color: selection.storageAvailable ? C.muted : "#865A12" }}>
              {selection.storageAvailable
                ? selectedCarriers.length ? "Selections saved in this browser for this tenant." : "No insurers selected. Choose one or more to plan your connections."
                : "Browser storage is unavailable. Selections will only last while this page stays open."}
            </Typography>
            {selectedCarriers.length > 0 && (
              <Button size="small" onClick={() => { updateSelection([]); inputRef.current?.focus(); }} aria-label="Clear all selected insurers"
                sx={{ textTransform: "none", minWidth: "auto", flexShrink: 0, px: 0.5, py: 0, fontSize: 12 }}>Clear all</Button>
            )}
          </Box>
        </Box>

        <Box id="planned-insurers-status" sx={{ borderRadius: "8px", p: 1.5, background: C.ground, border: `1px solid ${C.line}` }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: C.muted }}>Connection status</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.ink, mt: 0.5 }}>API integration planned</Typography>
          <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.5 }}>These demo selections do not connect to live carrier data.</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: 2, pt: 1.5, borderTop: `1px solid ${C.line}`, color: C.muted }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, mt: "1px", flexShrink: 0 }} aria-hidden="true" />
        <Typography sx={{ fontSize: 12 }}>Selection only — no authentication, data transfer, cover binding or DCL approval.</Typography>
      </Box>
    </Box>
  );
}
